import { api, uploadRaw } from '@/lib/api-client';
import type {
  AdminRentalListParams,
  AdminStats,
  AppConfig,
  ArticleDetail,
  ArticleListItem,
  ArticleWriteInput,
  BookDetail,
  BookListItem,
  BookListParams,
  BookWriteInput,
  CalendarMonth,
  CreateRentalInput,
  CustomerDetail,
  CustomerListItem,
  CustomerStatusFilter,
  CustomerTag,
  MyRentalListParams,
  PaymentMethod,
  PublicUser,
  ReminderChannel,
  ReminderLogDto,
  ReminderScanSummary,
  ReminderSettings,
  ReminderStatus,
  RentalDto,
  SessionPayload,
  WaitingListBook,
  BookRequestDto,
  CreateBookRequestInput,
  UpdateBookRequestStatusInput,
  PopularBookRequest,
  BookRequestListParams,
} from '@/types/api';

declare module '@/types/api' {
  interface AdminStats {
    activeDeposits: number;
  }
}

/**
 * One thin function per endpoint. Components never build URLs; they call these,
 * and the composables in src/composables wrap them in TanStack Query.
 */

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export const fetchAppConfig = () => api.get<AppConfig>('/pengaturan');

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const register = (input: {
  name: string;
  whatsappNumber: string;
  password: string;
  email: string;
  genres: string[];
}) => api.post<SessionPayload>('/auth/daftar', input);

export const login = (input: { whatsappNumber: string; password: string }) =>
  api.post<SessionPayload>('/auth/masuk', input);

export const logout = () => api.post<{ success: boolean }>('/auth/keluar');

export const fetchCurrentUser = () => api.get<{ user: PublicUser }>('/auth/saya');

export const updateProfile = (input: { name?: string; email?: string | null; genres?: string[] }) =>
  api.patch<{ user: PublicUser }>('/auth/saya', input);

export const changePassword = (input: { currentPassword: string; newPassword: string }) =>
  api.post<{ success: boolean; message: string }>('/auth/saya/kata-sandi', input);

export const requestPasswordReset = (input: { email: string }) =>
  api.post<{ message: string }>('/auth/lupa-kata-sandi', input);

export const verifyResetToken = (token: string) =>
  api.get<{ valid: boolean; email?: string }>('/auth/verifikasi-token-reset', { token });

export const resetPassword = (input: { token: string; newPassword: string }) =>
  api.post<{ message: string }>('/auth/reset-kata-sandi', input);


// ---------------------------------------------------------------------------
// Books
// ---------------------------------------------------------------------------

export const fetchBooks = (params: BookListParams, signal?: AbortSignal) =>
  api.list<BookListItem>('/buku', { ...params }, signal);

export const fetchBook = (id: string, signal?: AbortSignal) =>
  api.get<{ book: BookDetail }>(`/buku/${id}`, undefined, signal);

export const fetchGenres = (signal?: AbortSignal) =>
  api.get<{ genres: string[] }>('/buku/genre', undefined, signal);

export const joinWaitingList = (bookId: string) =>
  api.post<{ waitingListCount: number }>(`/buku/${bookId}/antrean`);

// `api.delete` discards the body, and this endpoint returns the new queue size,
// so it goes through the raw request and unwraps `{ data }` itself.
export const leaveWaitingList = async (bookId: string) => {
  const body = await api.raw<{ data: { waitingListCount: number } }>(
    `/buku/${bookId}/antrean`,
    { method: 'DELETE' },
  );
  return body.data;
};

export const adminRemoveFromWaitingList = async (bookId: string, customerId: string) => {
  const body = await api.raw<{ data: { waitingListCount: number } }>(
    `/buku/${bookId}/antrean/${customerId}`,
    { method: 'DELETE' },
  );
  return body.data;
};

export const markWaitingListReminded = (bookId: string, customerId: string) =>
  api.post<{ success: true }>(`/buku/${bookId}/antrean/${customerId}/ingatkan`);

export const unmarkWaitingListReminded = async (bookId: string, customerId: string) => {
  const body = await api.raw<{ data: { success: true } }>(
    `/buku/${bookId}/antrean/${customerId}/ingatkan`,
    { method: 'DELETE' },
  );
  return body.data;
};

export const fetchWaitingLists = (signal?: AbortSignal) =>
  api.get<{ books: WaitingListBook[] }>('/buku/antrean', undefined, signal);

export const fetchFeaturedBooks = (signal?: AbortSignal) =>
  api.get<{ newArrivals: BookListItem[]; editorPicks: BookListItem[] }>(
    '/buku/unggulan',
    undefined,
    signal,
  );

/**
 * Uploads a cover image and returns the path to store on the book.
 *
 * The file is sent as the raw request body rather than wrapped in FormData: the
 * endpoint takes one file and no other fields, so `body: file` is the whole
 * implementation on both sides.
 */
export const uploadCoverImage = (file: File) =>
  uploadRaw<{ coverImage: string; bytes: number; mimeType: string }>('/buku/sampul', file);

export const createBook = (input: BookWriteInput) =>
  api.post<{ book: BookDetail }>('/buku', input);

export const updateBook = (id: string, input: Partial<BookWriteInput>) =>
  api.patch<{ book: BookDetail }>(`/buku/${id}`, input);

export const deleteBook = (id: string) => api.delete(`/buku/${id}`);

// ---------------------------------------------------------------------------
// Rentals
// ---------------------------------------------------------------------------

export const createRental = (input: CreateRentalInput) =>
  api.post<{ rental: RentalDto }>('/peminjaman', input);

export const fetchMyRentals = (params: MyRentalListParams, signal?: AbortSignal) =>
  api.list<RentalDto>('/peminjaman/saya', { ...params }, signal);

export const fetchRentals = (params: AdminRentalListParams, signal?: AbortSignal) =>
  api.list<RentalDto>('/peminjaman', { ...params }, signal);

export const fetchRental = (id: string, signal?: AbortSignal) =>
  api.get<{ rental: RentalDto }>(`/peminjaman/${id}`, undefined, signal);

export async function fetchAdminStats(signal?: AbortSignal): Promise<{ stats: AdminStats }> {
  const stats = await api.get<AdminStats>('/peminjaman/statistik', undefined, signal);
  return { stats };
}

export const cancelRental = (id: string, reason?: string) =>
  api.post<{ rental: RentalDto }>(`/peminjaman/${id}/batal`, reason ? { reason } : {});

export const markManualReminder = (id: string, thresholdType: string) =>
  api.post<{ success: true }>(`/pengingat/manual/${id}`, { thresholdType });

export const unmarkManualReminder = async (id: string, thresholdType: string) => {
  const body = await api.raw<{ data: { success: true } }>(
    `/pengingat/manual/${id}/${thresholdType}`,
    { method: 'DELETE' },
  );
  return body.data;
};

export const approveRental = (id: string, input: { notes?: string }) =>
  api.post<{ rental: RentalDto }>(`/peminjaman/${id}/setujui`, input);

export const returnRental = (
  id: string,
  input?: {
    returnedAt?: string;
    bookTag?: string;
    bookTags?: string[];
    customerTags?: string[];
    bookNotes?: string;
    customerNotes?: string;
    transactionTag?: string;
    transactionTags?: string[];
    transactionNotes?: string;
    depositRefunded?: number;
    depositDeducted?: number;
  },
) => api.post<{ rental: RentalDto }>(`/peminjaman/${id}/kembali`, input || {});

export const updateRentalTransactionTag = (
  id: string,
  input: { transactionTag?: string; transactionTags?: string[]; transactionNotes?: string },
) => api.patch<{ rental: RentalDto }>(`/peminjaman/${id}/tag`, input);

export const payRental = (
  id: string,
  input: { paymentMethod: PaymentMethod; paidAmount?: number; discountAmount?: number; voucherIds?: string[]; notes?: string },
) => api.post<{ rental: RentalDto }>(`/peminjaman/${id}/bayar`, input);

export const rejectRental = (id: string, reason?: string) =>
  api.post<{ rental: RentalDto }>(`/peminjaman/${id}/tolak`, reason ? { reason } : {});

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

export const fetchArticles = (
  params: { page?: number; pageSize?: number; search?: string; includeUnpublished?: boolean },
  signal?: AbortSignal,
) =>
  api.list<ArticleListItem>(
    '/artikel',
    {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      // The backend expects the literal strings 'true' / 'false'.
      includeUnpublished: params.includeUnpublished ? 'true' : undefined,
    },
    signal,
  );

export const fetchArticle = (slug: string, signal?: AbortSignal) =>
  api.get<{ article: ArticleDetail }>(`/artikel/${slug}`, undefined, signal);

/** Same raw-body contract as book covers; see uploadCoverImage. */
export const uploadArticleCover = (file: File) =>
  uploadRaw<{ coverImage: string; bytes: number; mimeType: string }>('/artikel/sampul', file);

export const createArticle = (input: ArticleWriteInput) =>
  api.post<{ article: ArticleDetail }>('/artikel', input);

export const updateArticle = (id: string, input: Partial<ArticleWriteInput>) =>
  api.patch<{ article: ArticleDetail }>(`/artikel/id/${id}`, input);

export const deleteArticle = (id: string) => api.delete(`/artikel/id/${id}`);

// ---------------------------------------------------------------------------
// Customers (admin)
// ---------------------------------------------------------------------------

export const fetchCustomers = (
  params: { page?: number; pageSize?: number; search?: string; status?: CustomerStatusFilter },
  signal?: AbortSignal,
) => api.list<CustomerListItem>('/pelanggan', { ...params }, signal);

export const fetchCustomer = (id: string, signal?: AbortSignal) =>
  api.get<{ customer: CustomerDetail }>(`/pelanggan/${id}`, undefined, signal);

export const setCustomerActive = (id: string, isActive: boolean) =>
  api.patch<{ customer: CustomerListItem }>(`/pelanggan/${id}/status`, { isActive });

export const updateCustomer = (id: string, input: { nik?: string | null; customerTag?: CustomerTag | null }) =>
  api.patch<{ customer: CustomerListItem }>(`/pelanggan/${id}`, input);

export const verifyCustomerNik = (id: string, nik: string) =>
  api.patch<{ customer: CustomerListItem }>(`/pelanggan/${id}/verifikasi-nik`, { nik });

// ---------------------------------------------------------------------------
// Reminders
// ---------------------------------------------------------------------------

export const fetchReminderLogs = (
  params: {
    page?: number;
    pageSize?: number;
    rentalId?: string;
    status?: ReminderStatus;
    channel?: ReminderChannel;
  },
  signal?: AbortSignal,
) => api.list<ReminderLogDto>('/pengingat/log', { ...params }, signal);

export const fetchMyReminders = (
  params: { page?: number; pageSize?: number },
  signal?: AbortSignal,
) => api.list<ReminderLogDto>('/pengingat/saya', { ...params }, signal);

export const fetchReminderSettings = (signal?: AbortSignal) =>
  api.get<{ settings: ReminderSettings }>('/pengingat/pengaturan', undefined, signal);

export const runReminderScan = () =>
  api.post<{ accepted: boolean; summary: ReminderScanSummary | null }>('/pengingat/jalankan');

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

export const fetchAdminCalendar = (
  params: { year?: number; month?: number },
  signal?: AbortSignal,
) => api.get<{ calendar: CalendarMonth }>('/kalender', { ...params }, signal);

export const fetchMyCalendar = (params: { year?: number; month?: number }, signal?: AbortSignal) =>
  api.get<{ calendar: CalendarMonth }>('/kalender/saya', { ...params }, signal);

// ---------------------------------------------------------------------------
// Master Data
// ---------------------------------------------------------------------------

import type { MasterDataEntity, MasterType } from '@/types/api';

export const fetchMasterData = (type: MasterType, signal?: AbortSignal): Promise<MasterDataEntity[]> =>
  api.get<MasterDataEntity[]>(`/master-data/${type}`, undefined, signal);

export const createMasterData = (type: MasterType, input: { name: string; description?: string }) =>
  api.post<MasterDataEntity>(`/master-data/${type}`, input);

export const updateMasterData = (type: MasterType, id: string, input: { name: string; description?: string }) =>
  api.patch<MasterDataEntity>(`/master-data/${type}/${id}`, input);

export const deleteMasterData = (type: MasterType, id: string) =>
  api.delete(`/master-data/${type}/${id}`);

export interface AnalyticsData {
  revenue: { date: string; amount: number }[];
  totalRevenue?: number;
  totalDiscount?: number;
  totalDenda?: number;
  totalFinal?: number;
  genreStats: { genre: string; count: number; borrowCount: number }[];
  transactionCount: number;
  newMembersCount: number;
  totalBooks: number;
  totalUniqueBooksBorrowed: number;
  inventoryHealth: { available: number; borrowed: number };
  topBooks: { id: string; title: string; coverImage: string | null; borrowCount: number }[];
  bottomBooks: { id: string; title: string; coverImage: string | null; borrowCount: number }[];
}

export const fetchAnalytics = async (
  params?: { startDate?: string; endDate?: string },
  signal?: AbortSignal,
): Promise<AnalyticsData> => {
  return api.get(
    '/peminjaman/analytics',
    { startDate: params?.startDate, endDate: params?.endDate },
    signal
  );
};

// ---------------------------------------------------------------------------
// Book Requests
// ---------------------------------------------------------------------------

export const createBookRequest = (input: CreateBookRequestInput) =>
  api.post<{ bookRequest: BookRequestDto }>('/book-requests', input);

export const fetchMyBookRequests = (signal?: AbortSignal) =>
  api.get<{ bookRequests: BookRequestDto[] }>('/book-requests/saya', undefined, signal);

export const fetchBookRequests = (params: BookRequestListParams, signal?: AbortSignal) =>
  api.list<BookRequestDto>('/book-requests', { ...params }, signal);

export const fetchPopularBookRequests = (signal?: AbortSignal) =>
  api.get<{ popular: PopularBookRequest[] }>('/book-requests/populer', undefined, signal);

export const updateBookRequestStatus = (id: string, input: UpdateBookRequestStatusInput) =>
  api.patch<{ bookRequest: BookRequestDto }>(`/book-requests/${id}/status`, input);

export const deleteBookRequest = (id: string) =>
  api.delete(`/book-requests/${id}`);

